import { Component, type ReactNode } from "react";

export type AdministratorMfaFailureKind = "reauthentication-required" | "verification-unavailable";

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null;
}

export function classifyAdministratorMfaFailure(error: unknown): AdministratorMfaFailureKind {
  if (!isRecord(error) || !isRecord(error["data"])) return "verification-unavailable";
  return error["data"]["code"] === "REAUTHENTICATION_REQUIRED"
    ? "reauthentication-required"
    : "verification-unavailable";
}

export function AdministratorMfaFailure({
  failure,
  onReauthenticate,
}: Readonly<{
  failure: AdministratorMfaFailureKind;
  onReauthenticate: (() => Promise<void>) | null;
}>): React.JSX.Element {
  const reauthenticationRequired = failure === "reauthentication-required";
  return (
    <section className="mfa-gate" aria-live="assertive">
      <p className="eyebrow">Required administrator control</p>
      <h1>
        {reauthenticationRequired
          ? "Refresh your administrator session"
          : "Administrator verification unavailable"}
      </h1>
      <p className="mfa-intro">
        {reauthenticationRequired
          ? "For your security, Site Administration requires a recent WorkOS sign-in. Sign out, then sign in again to continue."
          : "Reminder Tracker could not safely verify this administrator session. No administration information has been shown."}
      </p>
      {onReauthenticate !== null ? (
        <button type="button" onClick={() => void onReauthenticate()}>
          {reauthenticationRequired
            ? "Sign out and reauthenticate"
            : "Return to administrator sign-in"}
        </button>
      ) : null}
    </section>
  );
}

type BoundaryProps = Readonly<{
  children: ReactNode;
  onReauthenticate: (() => Promise<void>) | null;
}>;

type BoundaryState = Readonly<{
  error?: unknown;
  hasError: boolean;
}>;

export class AdministratorMfaErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  public override state: BoundaryState = { hasError: false };

  public static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error, hasError: true };
  }

  public override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <AdministratorMfaFailure
          failure={classifyAdministratorMfaFailure(this.state.error)}
          onReauthenticate={this.props.onReauthenticate}
        />
      );
    }
    return this.props.children;
  }
}
